import { mixins } from 'vue-class-component';
import { Component, Watch } from 'vue-property-decorator';

import StoreModuleMixin from '@/mixins/StoreModuleMixin';
import { FilterableGridModuleState, INIT_GRID, PAGE_CARDS, M_UPDATE_MASONRY } from './FilterableGridModule.store';
import { getComponentProps } from '@/utils/vueUtils';
import { FilterableGridViewModel } from './FilterableGridModule.types';

import DottedLine from '@/assets/icons/dotted-line-2.svg?inline';

import FilterableGridHeader from './FilterableGridHeader/FilterableGridHeader.vue';
import FilterableGrid from './FilterableGrid/FilterableGrid.vue';
import FilterableGridMasonry from './FilterableGridMasonry/FilterableGridMasonry.vue';
import FilterableGridPaging from './FilterableGridPaging/FilterableGridPaging.vue';
import { CtaButtonStyle } from '@/common/interfaces/CtaLinkItem';
import { PageMeta, PAGE_META } from '@/store/modules/metadataModule';
import { SegmentElement } from '@/plugins/utm/SegmentElement';
import { Getter } from 'vuex-class';
import ItineraryCard from '@/common/interfaces/modules/ItineraryCard';

@Component({
  props: getComponentProps<FilterableGridViewModel>({
    Header: 'All destinations',
    ShowHeaderCount: true,
    ShowLeftResultCount: false,
    HeaderCta: { Title: 'Submit Orders', Link: '#' },
    Filters: [],
    CurrentSorting: 'nameAsc',
    Sorting: [],
    Cards: [],
    Masonry: {
      TopTitle: 'Latest',
      MainTitle: 'Previous Reports',
      MasonryField: 'IsLatest',
    },
    Paging: {
      Total: 100,
      PageSize: 12,
      PageSizeMedium: 8,
      PageSizeSmall: 4,
      ViewCounts: false,
      LoadMoreButton: { Title: 'ViewMore W&N', Link: '#', ButtonStyle: CtaButtonStyle.Tertiary },
    },
    Endpoint: '/api/',
    AppliedFilters: null
  }),
  components: {
    FilterableGridHeader,
    FilterableGrid,
    FilterableGridMasonry,
    FilterableGridPaging,
    DottedLine,
  },
})
export default class FilterableGridModule extends mixins(StoreModuleMixin) {
  p!: FilterableGridViewModel;

  state!: FilterableGridModuleState;

  isPageCardsChangedOnce = false;

  module: HTMLElement;

  get Cards() {
    return this.getters(PAGE_CARDS) as ItineraryCard[];
  }
  get Sorting() {
    return this.state?.CurrentSorting;
  }

  @Watch('Sorting')
  SortingChanged(Sorting: string): void {
    const newMasonry = {
      TopTitle: Sorting,
      MainTitle: Sorting === 'Oldest' ? 'Next' : 'Previous',
    }
    this.getMutation(M_UPDATE_MASONRY)(newMasonry)
  }

  @Getter(PAGE_META)
  PageMeta!: PageMeta;

  @Watch('state.IsLoading')
  IsLoadingChanged(newVal: boolean): void {
    if (!newVal && !this.isPageCardsChangedOnce) {
      this.productListViewed();
      this.isPageCardsChangedOnce = true;
    };
  }

  initGrid: () => void = this.getAction(INIT_GRID);

  mounted() {
    this.initGrid();
    this.module = this.$el.closest('*[contentlinkid]');
  }

  productListViewed() {
    if (this.Cards[0]?.CardType === 'Itinerary') {
      const products = this.Cards.map(card => {
        return  {
          brand: 'Lindblad Expeditions',
          image_url: card.Image.Url,
          name: card.Title,
          product_id: card.ProductType,
          ...(card.SpecialOffers.length && { offers: card.SpecialOffers.map(offer => ({ offer_name: offer.Question })) }),
          categories: card.Locations.map(category => ({ category })),
        }
      })
      SegmentElement.sendEvent('productListViewed', {
        category: this.PageMeta.Analytics.categories && this.PageMeta.Analytics.categories.length ? this.PageMeta.Analytics.categories[0].category: null,
        list_id: this.PageMeta.ContextMeta.Destination ? JSON.parse(this.PageMeta.ContextMeta.Destination).Id.toString() : null,
        nonInteraction: 1,
        products
      })
    }
    this.$nextTick(() => setTimeout(() => document.querySelectorAll('.filterable-grid__grid a').forEach(this.gridItemInteraction), 1000));
  }

  gridItemInteraction(link) {
    link.addEventListener('click', (e) => {
      const target = e.target as any;
      const parent = target.closest('.filterable-grid__grid-item');
      const segmentProps = { ...JSON.parse(parent.dataset.segment), text: target.innerText };
      if (Object.values(this.$attrs).length) {
        this.$emit('moduleClicked', segmentProps);
      } else {
        this.$parent.$emit('moduleClicked', segmentProps);
      }

      if (segmentProps.component_name === 'Itinerary') {
        const product_id = parent.getAttribute('id');
        const productBusiness = this.Cards.find(card => card.Id == product_id);
        const props = {
          currency: productBusiness.Price.includes('AU') ? 'AUD' : 'USD',
          image_url: productBusiness.Image.Url,
          trip_length: productBusiness.Days,
          price: +productBusiness.Price.replace(/\D/g,''),
          name: productBusiness.Title,
          categories: productBusiness.Locations.map(category => ({ category })),
          product_id: productBusiness.ProductType,
          position: segmentProps.position,
          brand: 'Lindblad Expeditions',
          module_type_id: this.module.getAttribute('data-segmentid'),
          module_type_name: this.module.getAttribute('type'),
          page_type_id: this.PageMeta.Analytics.page_type_id,
          page_type_name: this.PageMeta.Analytics.page_type_name,
        }
        SegmentElement.sendEvent('productClicked', props);
      }
    })
  }

  headerCtaClick() {
    this.$emit('headerCtaClick');
  }
  get AppliedFiltersMessage(){
    if (!this.p.AppliedFilters) return
    let filter = [];
    let message = '';
    if (this.p.AppliedFilters.Destination) {
      filter.push({name: 'Destination', value: this.p.AppliedFilters.Destination})
    }
    if (this.p.AppliedFilters.Itinerary) {
      filter.push({name: 'Itinerary', value: this.p.AppliedFilters.Itinerary})
    }
    if (this.p.AppliedFilters.StartDate) {
      filter.push({name: 'Start Date', value: this.p.AppliedFilters.StartDate})
      filter.push({name: 'End Date', value: this.p.AppliedFilters.EndDate})
    }
    if (filter.length){
      message = 'Applied filters: ';
      message += filter.map(x=> x.name + ' - '+ x.value).join(', ')
    }

    return message;
  }
}
